import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Bolt,
  Dangerous,
  ErrorWarningtraiangle,
  Info,
  More,
  Play,
  Reset,
  Stop,
  ocppStatusIcon,
} from '../../../assets/icons';
import { AnalyticsEvent, captureAnalyticsEvent } from '../../../constant/Events';
import { IconSize } from '../../../constant/IconSize.constant';
import { CHARGER_STATUS, OCPP_STATUS } from '../../../constant/Text.constant';
import { URL_SEARCH_PARAM_KEY } from '../../../constant/UrlSearchParams.constant';
import { USER_ROLE, useAuth } from '../../../hooks';
import { useCompany } from '../../../hooks/useCompany';
import { useCompanyPermissions } from '../../../hooks/useCompanyPermissions';
import { useAllowedFeatures } from '../../../hooks/useFeaturePersonalization';
import { RoutePath } from '../../../routes';
import { useDeleteChargerMutation, useRebootChargerMutation } from '../../../services/charger.api';
import { useLazyFetchCompanyDetailQuery } from '../../../services/company.api';
import { useDeleteHomeChargerMutation } from '../../../services/homecharger.api';
import { useClearProfileMutation } from '../../../services/pm.api';
import {
  useLazyFetchSessionsQuery,
  useSessionForceStopChargingMutation,
  useSessionStartChargingMutation,
  useSessionStopChargingMutation,
} from '../../../services/session.api';
import { getWebAppUrlPrefix } from '../../../stores/selectors/deployment.selector';
import { getCurrentTheme } from '../../../stores/selectors/theme.selector';
import { Charger, License } from '../../../stores/types';
import { ChargerPort, OWNER_TYPE, Session } from '../../../stores/types/sessions.interface';
import { convertToDate, getDifferenceInMinutes } from '../../../utils/Date.Util';
import { addSearchParam } from '../../../utils/SearchParam.Utils';
import { LICENSE_STATUS } from '../../Licenses/utils';
import { handleTimeCalculation } from '../../Session/utils';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  Divider,
  Icon,
  Label,
  LabelType,
  MODAL_TYPES,
  Menu,
  MenuItem,
  Skeleton,
  useGlobalModalContext,
} from '../../_ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../_ui/Tooltip.component';
import { SplitButton } from '../../_ui/split-button/SplitButton.component';
import { ChargerStatus } from '../ChargerStatus.component';
import ConnectorRowItem from '../ConnectorRowItem.component';
import { ViewQRCode } from '../ViewQRCode.component';
import { NearbyChargerFinder } from '../charger-finder/NearbyChargerFinder.component';
import { ChargerCompany, ChargerConfirmation, ChargerSettings } from '../move-charger';
import { getConnectorAbbr, getConnectorIcon, getValidRawStatus } from '../utils';
import { ChargerError } from './ChargerError.component';
import { ChargerLicenseExpired } from './ChargerLicenseExpired.component';
import { PMEditMessage } from './PMEditMessage.component';
import { SparkAnalyze } from './SparkAnalyze.component';

type ChargerSummaryProps = {
  charger: Charger;
  license: License | null;
  isLoading: boolean;
  companyId?: string;
};

export const ChargerSummary = ({
  charger,
  license,
  isLoading,
  companyId: chargerCompanyId,
}: ChargerSummaryProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const theme = useSelector(getCurrentTheme);
  const { allowSparkAnalyze } = useAllowedFeatures();
  const { chargerId: id } = useParams();
  const { showModal } = useGlobalModalContext();

  const { role } = useAuth();
  const { switchCompany, userScope, isMultiViewActive, companyScope } = useCompany();
  const {
    canTriggerChargingSessions,
    canRebootChargers: hasRebootPermissionsOnCharger,
    canUseSparkChargerAnalysis,
    canMoveChargers,
    canManagePowerManagement,
    canDeleteChargers: hasDeletePermissionsOnCharger,
  } = useCompanyPermissions(chargerCompanyId);
  const canRebootChargers = hasRebootPermissionsOnCharger || role === USER_ROLE.SUPPORT;
  const canDeleteChargers = hasDeletePermissionsOnCharger || role === USER_ROLE.SUPPORT;

  const [rebootCharger] = useRebootChargerMutation();
  const [sessionStart] = useSessionStartChargingMutation();
  const [sessionStop] = useSessionStopChargingMutation();
  const [sessionForceStop] = useSessionForceStopChargingMutation();
  const [chargerSessions, setChargerSessions] = useState<Session[]>([]);
  const [triggerFetchCompanyDetail, { data: company }] = useLazyFetchCompanyDetailQuery();
  const [triggerFetchSessions, { data: sessionData }] = useLazyFetchSessionsQuery();

  const webAppUrlPrefix = useSelector(getWebAppUrlPrefix);

  const isAnyPortCharging = useMemo(() => {
    return charger?.ports?.some((port) => port?.status === 'CHARGING');
  }, [charger]);

  useEffect(() => {
    if (sessionData) {
      const sessions = sessionData.entities.filter(
        (session) => !session.stopTime && !session.completeTime,
      );
      setChargerSessions(sessions);
    }
  }, [sessionData]);

  const getIsSinglePort = useCallback(() => {
    return (charger?.ports?.length || 0) <= 1;
  }, [charger]);

  const handleViewChargerClick = () => {
    window.open(`${webAppUrlPrefix}/${charger?.name}`);
  };

  const handleViewQRCodeClick = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('view_qr_code'),
      width: '540px',
      height: 'max-content',
      shouldCloseOnOverlayClick: false,
      onRenderBody: () => (
        <ViewQRCode chargerName={charger?.name || ''} webAppUrl={webAppUrlPrefix} />
      ),
    });
  };

  const handleFindNearbyChargersClick = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('nearby_chargers_title'),
      width: 'min(1200px, calc(100vw - 80px))',
      height: 'max-content',
      overflow: 'hidden',
      shouldCloseOnOverlayClick: false,
      onRenderBody: () => <NearbyChargerFinder charger={charger} />,
    });
  };

  const handleChargerMove = () => {
    showModal(MODAL_TYPES.WIZARD_MODAL, {
      width: '540px',
      height: 'max-content',
      defaultOverflow: 'auto',
      stepTitles: [
        t('charger_move_step_1_title'),
        t('charger_move_step_2_title'),
        t('charger_move_step_3_title'),
      ],
      stepComponents: [
        // eslint-disable-next-line react/jsx-key
        <ChargerCompany chargerInfo={charger} companyInfo={company} />,
        // eslint-disable-next-line react/jsx-key
        <ChargerSettings />,
        // eslint-disable-next-line react/jsx-key
        <ChargerConfirmation />,
      ],
      shouldCloseOnOverlayClick: false,
      onClose: () => {
        switchCompany(chargerCompanyId!, RoutePath.CHARGERS);
      },
    });
  };

  const showClearProfileAlert = () => {
    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('are_you_sure'),
      message: (
        <Trans
          i18nKey='pm_clear_profile_desc'
          components={{
            br: <br />,
          }}
        />
      ),
      mutationHook: useClearProfileMutation,
      mutationParams: {
        scope: companyScope,
        chargerId: charger.id,
      },
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.NEGATIVE,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
        },
        {
          label: t('clear_profile'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          isSubmit: true,
        },
      ],
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleDeleteCharger = () => {
    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('charger_delete_warn_title'),
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.NEGATIVE,
      message: (
        <Trans
          i18nKey={t('charger_delete_warn_message')}
          components={{
            bold: <strong />,
          }}
        />
      ),
      mutationHook:
        charger?.ownerType === OWNER_TYPE.USER
          ? useDeleteHomeChargerMutation
          : useDeleteChargerMutation,
      mutationParams: {
        id,
        name: charger?.name,
        scope: userScope!,
        ...(chargerCompanyId && { companyId: chargerCompanyId }),
      },
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
        },
        {
          label: t('delete'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          disableDelayTime: 3000,
          disableDelayLabel: t('wait_3_sec'),
          className: 'min-w-fit w-20',
          isSubmit: true,
          dataTestId: 'delete-charger-button',
        },
      ],
      onClose: (qResult: any) => {
        if (qResult?.isSuccess) {
          switchCompany(isMultiViewActive ? 'ALL' : chargerCompanyId!, RoutePath.CHARGERS);
        }
      },
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleRefresh = useCallback(() => {
    window.location.replace(
      `${addSearchParam(
        `../${RoutePath.CHARGERS}/${id}`,
        URL_SEARCH_PARAM_KEY.COMPANY_ID,
        isMultiViewActive ? 'ALL' : chargerCompanyId || '',
      )}`,
    );
  }, [id, isMultiViewActive, chargerCompanyId]);

  const showSpinnerAlert = useCallback(() => {
    showModal(MODAL_TYPES.SPINNER_MODAL, {
      delay: 5000,
      onClose: handleRefresh,
    });
  }, [showModal, handleRefresh]);

  const onRebootClick = useCallback(
    (chargerId?: string) => {
      if (chargerId) {
        rebootCharger({
          id: chargerId,
          scope: userScope!,
          ...(chargerCompanyId && { companyId: chargerCompanyId }),
        });
        captureAnalyticsEvent(AnalyticsEvent.CHARGER_REBOOT_EVENT, {
          chargerId,
        });
        showSpinnerAlert();
      }
    },
    [rebootCharger, userScope, chargerCompanyId, showSpinnerAlert],
  );

  const handleRebootClick = useCallback(() => {
    const title = t('charer_reboot_charger');
    showModal(MODAL_TYPES.ALERT_MODAL, {
      title,
      message: t('charger_reboot_msg'),
      icon: Info,
      iconColor: ColorType.WARNING,
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
        },
        {
          label: t('reset'),
          type: ButtonType.PRIMARY,
          style: { backgroundColor: theme.navigationSelectedColor },
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
          onClick: () => onRebootClick(charger?.id),
        },
      ],
    });
  }, [t, showModal, onRebootClick, charger?.id, theme.navigationSelectedColor]);

  const onStartClick = useCallback(
    (portId?: string) => {
      if (portId) {
        sessionStart({
          portId,
          scope: userScope!,
          ...(chargerCompanyId && { companyId: chargerCompanyId }),
        });
        showSpinnerAlert();
      }
    },
    [sessionStart, userScope, chargerCompanyId, showSpinnerAlert],
  );

  const onStopClick = useCallback(
    (portId?: string) => {
      const sessionId = chargerSessions?.find((session: Session) => session.port.id === portId)?.id;

      if (sessionId) {
        sessionStop({
          sessionId,
          scope: userScope!,
          ...(chargerCompanyId && { companyId: chargerCompanyId }),
        });
        showSpinnerAlert();
      }
    },
    [sessionStop, userScope, chargerCompanyId, chargerSessions, showSpinnerAlert],
  );

  const onForceStopClick = useCallback(
    (portId?: string) => {
      const sessionId = chargerSessions?.find((session: Session) => session.port.id === portId)?.id;

      if (sessionId) {
        sessionForceStop({
          sessionId,
          scope: userScope!,
          ...(chargerCompanyId && { companyId: chargerCompanyId }),
        });
        showSpinnerAlert();
      }
    },
    [sessionForceStop, userScope, chargerCompanyId, chargerSessions, showSpinnerAlert],
  );

  const handleStartClick = useCallback(
    (portId: string, portPhysicalLocation: string) => {
      const title = t('charger_start_charging');
      showModal(MODAL_TYPES.ALERT_MODAL, {
        height: 'max-content',
        title,
        message: getIsSinglePort() ? (
          t('charger_start_msg')
        ) : (
          <Trans
            i18nKey='multiple_port_charger_start_msg'
            values={{
              physicalLocation: t(`${portPhysicalLocation.toLowerCase()}`),
            }}
          />
        ),
        icon: Info,
        buttons: [
          {
            label: t('cancel'),
            type: ButtonType.TERTIARY,
            size: ButtonSize.SMALL,
            className: 'min-w-fit w-20',
          },
          {
            label: t('start'),
            type: ButtonType.PRIMARY,
            style: { backgroundColor: theme.navigationSelectedColor },
            size: ButtonSize.SMALL,
            className: 'min-w-fit w-20',
            onClick: () => onStartClick(portId),
          },
        ],
      });
    },
    [t, showModal, getIsSinglePort, onStartClick, theme.navigationSelectedColor],
  );

  const handleStopClick = useCallback(
    (portId: string, portPhysicalLocation: string) => {
      const title = t('charger_stop_charging');
      showModal(MODAL_TYPES.ALERT_MODAL, {
        height: 'max-content',
        title,
        message: getIsSinglePort() ? (
          t('charger_stop_msg')
        ) : (
          <Trans
            i18nKey='multiple_port_charger_stop_msg'
            values={{
              physicalLocation: t(`${portPhysicalLocation.toLowerCase()}`),
            }}
          />
        ),
        icon: ErrorWarningtraiangle,
        iconColor: ColorType.NEGATIVE,
        buttons: [
          {
            label: t('cancel'),
            type: ButtonType.TERTIARY,
            size: ButtonSize.SMALL,
            className: 'min-w-fit w-20',
          },
          {
            label: t('stop'),
            type: ButtonType.DESTRUCTIVE,
            size: ButtonSize.SMALL,
            className: 'min-w-fit w-20',
            onClick: () => onStopClick(portId),
          },
        ],
      });
    },
    [t, showModal, getIsSinglePort, onStopClick],
  );

  const handleForceStopClick = useCallback(
    (portId: string, portPhysicalLocation: string) => {
      const title = t('charger_force_stop_charging');
      showModal(MODAL_TYPES.ALERT_MODAL, {
        height: 'max-content',
        title,
        message: getIsSinglePort() ? (
          t('charger_force_stop_msg')
        ) : (
          <Trans
            i18nKey='multiple_port_charger_force_stop_msg'
            values={{
              physicalLocation: t(`${portPhysicalLocation.toLowerCase()}`),
              chargerName: charger?.name || '',
            }}
          />
        ),
        icon: ErrorWarningtraiangle,
        iconColor: ColorType.NEGATIVE,
        buttons: [
          {
            label: t('cancel'),
            type: ButtonType.TERTIARY,
            size: ButtonSize.SMALL,
            className: 'min-w-fit w-20',
          },
          {
            label: t('force_stop'),
            type: ButtonType.DESTRUCTIVE,
            size: ButtonSize.SMALL,
            className: 'min-w-fit w-20',
            onClick: () => onForceStopClick(portId),
          },
        ],
      });
    },
    [t, showModal, getIsSinglePort, onForceStopClick],
  );

  const renderSummaryHeader = () => {
    const hasBottomMenuItems = canMoveChargers || canManagePowerManagement || canDeleteChargers;

    return (
      <div className='flex flex-row relative justify-between items-center'>
        <Tooltip placement='top'>
          <TooltipTrigger>
            <Label
              text={charger?.name || ''}
              type={LabelType.H4}
              color={ColorType.BLACK}
              isLoading={isLoading}
              skeletonHeight='h-6'
              className='truncate max-w-[364px]'
            />
          </TooltipTrigger>
          <TooltipContent style={{ maxWidth: '420px', wordWrap: 'break-word' }}>
            <Label text={charger?.name || ''} type={LabelType.BODY3} color={ColorType.WHITE} />
          </TooltipContent>
        </Tooltip>

        <div className='absolute right-0'>
          <Menu placement='bottom-end' icon={More} dataTestId='chargers-details-menu'>
            <MenuItem label={t('view_charger_as_driver')} onClick={handleViewChargerClick} />
            <MenuItem label={t('view_qr_code')} onClick={handleViewQRCodeClick} />
            {role === USER_ROLE.SUPPORT && (
              <MenuItem
                label={t('nearby_chargers_menu_item')}
                onClick={handleFindNearbyChargersClick}
                dataTestId='chargers-details-find-nearby'
              />
            )}
            {hasBottomMenuItems && <Divider margin='2px' />}
            {canMoveChargers && (
              <MenuItem
                label={t('move_charger')}
                onClick={handleChargerMove}
                disabled={charger?.isPowerManaged}
              />
            )}
            {canManagePowerManagement && (
              <MenuItem
                label={t('pm_clear_profile')}
                labelColor={ColorType.NEGATIVE}
                onClick={showClearProfileAlert}
                disabled={
                  charger?.isPowerManaged ||
                  charger.status === CHARGER_STATUS.COMING_SOON ||
                  charger.status === CHARGER_STATUS.OFFLINE
                }
              />
            )}
            {canDeleteChargers && (
              <MenuItem
                label={t('delete')}
                labelType={LabelType.BODY3}
                labelColor={ColorType.NEGATIVE}
                onClick={handleDeleteCharger}
                disabled={
                  (charger?.isPowerManaged && charger.ownerType !== OWNER_TYPE.USER) ||
                  isAnyPortCharging
                }
                dataTestId='chargers-details-delete-button'
              />
            )}
          </Menu>
        </div>
      </div>
    );
  };

  const renderChargerImage = useMemo(() => {
    if (isLoading) {
      return <Skeleton width='w-[109px]' height='h-[109px]' />;
    }

    if (!charger?.imageUrl) return null;

    return (
      <div className='flex self-center !w-[109px] !h-[109px]'>
        <img src={charger?.imageUrl} className='object-contain w-full h-full' alt='' />
      </div>
    );
  }, [charger, isLoading]);

  const renderRebootButton = useMemo(() => {
    if (!canRebootChargers) {
      return null;
    }
    return (
      <Button
        label={t('reboot')}
        labelIconSize={IconSize.SIZE_16x16}
        type={ButtonType.OUTLINE}
        icon={Reset}
        size={ButtonSize.MINI_FULL}
        onClick={handleRebootClick}
      />
    );
  }, [canRebootChargers, handleRebootClick, t]);

  const connectorStatus = useCallback(
    (port: ChargerPort) => {
      // Single port charger that have no active transaction should return charger platform status for displaying in status pill(COR-183)
      if (charger?.ports?.length === 1 && charger.status !== OCPP_STATUS.CHARGING) {
        return charger.status;
      }
      // Multiport charger : If port have active transaction should return OCPP status else should return port status
      return port.status.toLowerCase() === OCPP_STATUS.CHARGING
        ? getValidRawStatus(port?.rawChargerStatus?.rawChargerStatus?.toLowerCase())
        : port.status.toLowerCase();
    },
    [charger],
  );

  const calculateMin = (date: string) => {
    return getDifferenceInMinutes(new Date(), convertToDate(date));
  };

  const renderOcppStatus = useCallback(
    (port: ChargerPort) => {
      if (
        !port?.rawChargerStatus?.rawChargerStatus ||
        charger.status.toLowerCase() === CHARGER_STATUS.OFFLINE
      ) {
        return null;
      }

      const mins = calculateMin(port.rawChargerStatus?.rawChargerStatusChangedTime);

      const rawStatus = getValidRawStatus(port.rawChargerStatus?.rawChargerStatus);
      let text = t(rawStatus?.toLocaleLowerCase());
      if (rawStatus.toLowerCase() === OCPP_STATUS.SUSPENDED_EV) {
        text = 'SuspendedEV';
      } else if (rawStatus.toLowerCase() === OCPP_STATUS.SUSPENDED_EVSE) {
        text = 'SuspendedEVSE';
      } else if (rawStatus.toLowerCase() === OCPP_STATUS.FINISHING) {
        text = 'Finishing';
      }

      return (
        <ConnectorRowItem
          icon={ocppStatusIcon}
          leftText={t('ocpp_status')}
          rightText={`${text} (~${handleTimeCalculation(mins, true)})`}
        />
      );
    },
    [charger, t],
  );

  const renderConnectors = useMemo(() => {
    return (
      <div className='flex flex-col gap-2  w-full'>
        {isLoading && <Skeleton width='w-70' height='h-39' />}
        {!isLoading &&
          charger?.ports?.map((port) => {
            return (
              <div
                key={port.id}
                className='flex flex-col gap-3 justify-between bg-grey0 p-2 rounded-lg'
              >
                <div className='flex flex-col gap-3'>
                  <div className='flex flex-row items-center justify-between'>
                    {charger?.ports?.length > 1 && ( // BF-677 hide port position for single port
                      <div className='w-[3.75rem]'>
                        <Label
                          text={t(`${port.physicalLocation.toLowerCase()}`)}
                          type={LabelType.LABEL_S}
                          color={ColorType.BLACK}
                        />
                      </div>
                    )}
                    <div className='flex flex-row items-center'>
                      <ChargerStatus
                        status={connectorStatus(port)}
                        timeStamp={calculateMin(port.rawChargerStatus?.rawChargerStatusChangedTime)}
                      />
                    </div>
                  </div>
                  <div>
                    <ConnectorRowItem
                      leftText={t('session_connector')}
                      rightText={getConnectorAbbr(port.connectorTypes[0])}
                      renderIcon={port.connectorTypes.map((type) => (
                        <Icon
                          key={type}
                          size={IconSize.SIZE_16x16}
                          src={getConnectorIcon(type)}
                          color={ColorType.BLACK}
                          className='mr-1'
                        />
                      ))}
                    />
                    <ConnectorRowItem
                      leftText={t('speed')}
                      rightText={`${port.maxWattage} ${t('kw_maximum')}`}
                      icon={Bolt}
                    />

                    {renderOcppStatus(port)}
                  </div>
                </div>
                {canTriggerChargingSessions && (
                  <div className='flex flex-row items-center h-8 justify-between gap-2'>
                    <Button
                      label={t('start')}
                      type={ButtonType.OUTLINE}
                      icon={Play}
                      iconColor={ColorType.POSITIVE}
                      size={ButtonSize.MINI}
                      onClick={() => handleStartClick(port.id, port.physicalLocation)}
                      buttonStyle={{ borderWidth: 1, width: '100%' }}
                      labelContainerCss='label-flex-g-0'
                    />
                    {role === USER_ROLE.SUPPORT ? (
                      <SplitButton
                        type={ButtonType.OUTLINE}
                        size={ButtonSize.MINI}
                        options={[
                          {
                            label: t('stop'),
                            icon: Stop,
                            iconColor: ColorType.NEGATIVE,
                            onClick: () => handleStopClick(port.id, port.physicalLocation),
                          },
                          {
                            label: t('force_stop'),
                            icon: Dangerous,
                            iconColor: ColorType.NEGATIVE,
                            onClick: () => handleForceStopClick(port.id, port.physicalLocation),
                          },
                        ]}
                        dataTestId='stop-split-button'
                      />
                    ) : (
                      <Button
                        label={t('stop')}
                        type={ButtonType.OUTLINE}
                        icon={Stop}
                        iconColor={ColorType.NEGATIVE}
                        size={ButtonSize.MINI}
                        onClick={() => handleStopClick(port.id, port.physicalLocation)}
                        buttonStyle={{ borderWidth: 1, width: '100%' }}
                        labelContainerCss='label-flex-g-0'
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  }, [
    charger,
    isLoading,
    canTriggerChargingSessions,
    handleStartClick,
    handleStopClick,
    handleForceStopClick,
    role,
    t,
    connectorStatus,
    renderOcppStatus,
  ]);

  const renderSparkAnalyze = () => {
    return (
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <div className='flex flex-row gap-1'>
            <Label
              text={t('spark_header_label')}
              type={LabelType.LABEL_M}
              color={ColorType.GREY5}
            />
            <Tooltip placement='top'>
              <TooltipTrigger>
                <Icon src={Info} size={IconSize.SIZE_20x20} />
              </TooltipTrigger>
              <TooltipContent>
                <Label
                  text={t('spark_header_tooltip')}
                  type={LabelType.LABEL_S_MEDIUM}
                  color={ColorType.WHITE}
                />
              </TooltipContent>
            </Tooltip>
          </div>
          <Label text={t('spark_desc_label')} type={LabelType.BODY3} color={ColorType.GREY6} />
        </div>
        <SparkAnalyze charger={charger} isModelView={false} />
      </div>
    );
  };

  useEffect(() => {
    if (chargerCompanyId) {
      triggerFetchCompanyDetail({ id: chargerCompanyId, scope: userScope! });
    }
  }, [chargerCompanyId, auth, triggerFetchCompanyDetail, userScope]);

  useEffect(() => {
    if (chargerCompanyId || isMultiViewActive) {
      triggerFetchSessions({
        chargerIds: id ? [id] : [],
        sort: 'desc',
        scope: userScope!,
        ...(chargerCompanyId && !isMultiViewActive && { companyId: chargerCompanyId }),
      });
    }
  }, [chargerCompanyId, userScope, isMultiViewActive, triggerFetchSessions, id]);

  return (
    <div className='w-[453px]'>
      <Card OnRenderTitle={renderSummaryHeader}>
        {auth.role === USER_ROLE.SUPPORT &&
          license !== null &&
          license.status.toLowerCase() === LICENSE_STATUS.EXPIRED && <ChargerLicenseExpired />}
        {charger?.isPowerManaged && <PMEditMessage />}
        <ChargerError chargerStatus={charger?.status} />
        <div className='flex flex-row gap-2'>
          <div className='flex flex-col gap-2'>
            {renderChargerImage}
            {renderRebootButton}
          </div>
          {renderConnectors}
        </div>

        {canUseSparkChargerAnalysis && (auth.role === USER_ROLE.SUPPORT || allowSparkAnalyze) && (
          <>
            <Divider margin='12px' />
            {renderSparkAnalyze()}
          </>
        )}
      </Card>
    </div>
  );
};
